import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Loader2, Mail } from "lucide-react";
import { useDataProvider, useNotify, useTranslate } from "ra-core";
import { useForm, type SubmitHandler } from "react-hook-form";
import { Navigate } from "react-router";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import type { CrmDataProvider } from "../providers/types";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { SignUpData } from "../types";
import { LoginSkeleton } from "./LoginSkeleton";
import { Notification } from "@/components/admin/notification";
import { SSOAuthButton } from "./SSOAuthButton";
import { googleWorkplaceDomain } from "./authConfig";

export const SignupPage = () => {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const { darkModeLogo: logo, title } = useConfigurationContext();
  const translate = useTranslate();
  const notify = useNotify();

  const [linkSent, setLinkSent] = useState(false);
  const [signupEmail, setSignupEmail] = useState("");

  const { data: isInitialized, isPending } = useQuery({
    queryKey: ["init"],
    queryFn: async () => {
      return dataProvider.isInitialized();
    },
  });

  const { isPending: isSignUpPending, mutate } = useMutation({
    mutationKey: ["signup"],
    mutationFn: async (data: SignUpData) => {
      return dataProvider.signUp(data);
    },
    onSuccess: (data) => {
      setSignupEmail(data.email);
      setLinkSent(true);
    },
    onError: (error) => {
      notify(error.message);
    },
  });

  const {
    register,
    handleSubmit,
    formState: { isValid },
  } = useForm<SignUpData>({
    mode: "onChange",
  });

  if (isPending) {
    return <LoginSkeleton />;
  }

  if (isInitialized) {
    return <Navigate to="/login" />;
  }

  const onSubmit: SubmitHandler<SignUpData> = async (data) => {
    mutate(data);
  };

  return (
    <div className="h-screen p-8">
      <div className="flex items-center gap-4">
        <img src={logo} alt={title} width={24} className="rounded" />
        <h1 className="text-xl font-semibold">{title}</h1>
      </div>
      <div className="h-full">
        <div className="max-w-sm mx-auto h-full flex flex-col justify-center gap-4">
          <h1 className="text-2xl font-bold mb-4">
            {translate("crm.auth.welcome_title", {
              _: "Welcome to Volt Hamburg Hub",
            })}
          </h1>
          <p className="text-base mb-4">
            {translate("crm.auth.signup.create_first_user", {
              _: "Create the first user account to complete the setup.",
            })}
          </p>
          {linkSent ? (
            <div className="flex flex-col items-center gap-4 py-4">
              <Mail className="h-12 w-12 text-muted-foreground" />
              <p className="text-center text-sm text-muted-foreground">
                {translate("crm.auth.magic_link_sent", {
                  _: "We sent a login link to",
                })}{" "}
                <strong>{signupEmail}</strong>.{" "}
                {translate("crm.auth.magic_link_check", {
                  _: "Check your inbox and click the link to sign in.",
                })}
              </p>
              <button
                type="button"
                className="text-sm hover:underline cursor-pointer"
                onClick={() => {
                  setLinkSent(false);
                  setSignupEmail("");
                }}
              >
                {translate("crm.auth.magic_link_retry", {
                  _: "Use a different email",
                })}
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="flex flex-col gap-2">
                <Label htmlFor="first_name">
                  {translate("crm.auth.first_name")}
                </Label>
                <Input
                  {...register("first_name", { required: true })}
                  id="first_name"
                  type="text"
                  required
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="last_name">
                  {translate("crm.auth.last_name")}
                </Label>
                <Input
                  {...register("last_name", { required: true })}
                  id="last_name"
                  type="text"
                  required
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="email">{translate("ra.auth.email")}</Label>
                <Input
                  {...register("email", { required: true })}
                  id="email"
                  type="email"
                  required
                />
              </div>
              <div className="flex flex-col gap-4 justify-between items-center mt-8">
                <Button
                  type="submit"
                  disabled={!isValid || isSignUpPending}
                  className="w-full"
                >
                  {isSignUpPending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      {translate("crm.auth.signup.creating", {
                        _: "Creating...",
                      })}
                    </>
                  ) : (
                    translate("crm.auth.signup.create_account", {
                      _: "Create account",
                    })
                  )}
                </Button>
                {googleWorkplaceDomain ? (
                  <SSOAuthButton
                    className="w-full"
                    domain={googleWorkplaceDomain}
                  >
                    {translate("crm.auth.sign_in_google_workspace", {
                      _: "Sign in with Google Workplace",
                    })}
                  </SSOAuthButton>
                ) : null}
              </div>
            </form>
          )}
        </div>
      </div>
      <Notification />
    </div>
  );
};

SignupPage.path = "/sign-up";
